'use client';

import { useCallback, useEffect, useRef, useState, type ReactElement } from 'react';
import { useTranslations } from '@fshr/context/i18n-context';
import { imgOpt } from '@fshr/shared';
import { cn } from '@fshr/shared-react';
import getCtaStyles from '../button/get-cta-styles';
import BevSafeText from '../bev-safe-text';
import Icon from '../icon';
import Image from '../image';
import YouTubeEmbed from '../youtube-embed';
import type { SlideshowCardData } from './types';

type Props = {
	card: SlideshowCardData;
	isActive: boolean;
	shouldPlayVideo: boolean;
};

export default function SlideshowCard({ card, isActive, shouldPlayVideo }: Props): ReactElement {
	const translate = useTranslations();
	const videoRef = useRef<HTMLVideoElement>(null);
	const [videoFailed, setVideoFailed] = useState(false);
	const [embedFailed, setEmbedFailed] = useState(false);
	const [isVideoPaused, setIsVideoPaused] = useState(false);

	// Only YouTube has a player here. Vidyard and Youku are authorable in Blacklight
	// but unimplemented, so they fall through to the image rather than render blank.
	const hasEmbed = card.videoSource === 'youtube' && Boolean(card.videoId) && !embedFailed;
	const hasVideo = !hasEmbed && Boolean(card.videoUrl || card.mobileVideoUrl) && !videoFailed;
	const isVideoPlaying = isActive && shouldPlayVideo && !isVideoPaused;

	const hasImage = Boolean(card.image?.defaultImage?.path);
	const hasMobileImage = Boolean(card.mobileImage?.defaultImage?.path);

	const onEmbedUnavailable = useCallback(() => setEmbedFailed(true), []);
	const onToggleVideo = useCallback(() => setIsVideoPaused(paused => !paused), []);

	const Title = card.headingLevel ?? 'h3';

	const posterSrc = card.image?.defaultImage?.path
		? imgOpt({
				path: card.image.defaultImage.path,
				cropping: card.image.defaultImage.cropping,
			})
		: undefined;

	useEffect(() => {
		const video = videoRef.current;
		if (!video || !hasVideo) return;

		if (isVideoPlaying) {
			video.play().catch(() => setVideoFailed(true));
		} else {
			video.pause();
		}
	}, [hasVideo, isVideoPlaying]);

	useEffect(() => {
		const video = videoRef.current;
		if (!video || isActive) return;
		video.currentTime = 0;
	}, [isActive]);

	return (
		<li className="relative w-[var(--card-w)] shrink-0" aria-hidden={!isActive}>
			<div className="relative aspect-[3/4] w-full overflow-hidden md:aspect-[2/1]">
				{hasImage && (
					<Image
						roughSize="l"
						imagePrimitive={card.image}
						className={cn(
							'absolute inset-0 h-full w-full object-cover',
							hasMobileImage && 'hidden md:block'
						)}
					/>
				)}
				{hasMobileImage && (
					<Image
						roughSize="l"
						imagePrimitive={card.mobileImage}
						className="absolute inset-0 h-full w-full object-cover md:hidden"
					/>
				)}

				{hasVideo && (
					<video
						ref={videoRef}
						className="absolute inset-0 h-full w-full object-cover"
						poster={posterSrc}
						muted
						loop
						playsInline
						preload="metadata"
						aria-hidden="true"
						onError={() => setVideoFailed(true)}
					>
						{card.mobileVideoUrl && (
							<source media="(max-width: 767px)" src={card.mobileVideoUrl} type="video/mp4" />
						)}
						{card.videoUrl && <source src={card.videoUrl} type="video/mp4" />}
					</video>
				)}

				{hasEmbed && card.videoId && (
					<YouTubeEmbed
						videoId={card.videoId}
						title={card.title}
						description={card.videoDescription}
						shouldPlay={isVideoPlaying}
						autoplay
						onUnavailable={onEmbedUnavailable}
					/>
				)}

				{/* Figma "play button, text" (mobile): button stacked flush on top of the
				    panel (no gap), right-aligned; the 50x50 hit area's own 15px padding
				    is the icon's inset. On desktop ("Frame 4759") it's pinned to the card's
				    bottom-left corner with card-padding-s on the left and bottom only,
				    so that padding is both the 40px icon's inset and the hit area. At both
				    sizes the icon sits on its own img-overlay-40 circle ("Ellipse 1"), so the
				    background goes on the icon, not the padded button (IAI-361). */}
				<div className="absolute inset-x-0 bottom-0 z-20 flex flex-col items-end md:contents">
					{(hasVideo || hasEmbed) && (
						<button
							type="button"
							onClick={onToggleVideo}
							tabIndex={isActive ? 0 : -1}
							aria-label={isVideoPlaying ? translate('Pause video') : translate('Play video')}
							className="flex size-500 items-center justify-center text-text-on-image-primary focus-visible:outline-2 focus-visible:outline-offset-2 md:absolute md:bottom-0 md:left-0 md:z-20 md:size-auto md:pb-card-padding-s md:pl-card-padding-s"
						>
							<Icon
								icon={isVideoPlaying ? 'pause_button_large' : 'play_button_large'}
								className="size-200 rounded-full bg-img-overlay-40 text-[20px] md:size-400 md:text-[40px]"
							/>
						</button>
					)}

					{/* Figma "gallery widget v2/inline": the gap is bound to stack-s on
					    mobile (stacked) and inline-m on desktop (side by side); padding is
					    card-padding-xs and "Text" itemSpacing is stack-2xs at both. */}
					<div className="relative flex w-full flex-col items-start justify-center gap-y-stack-s bg-surface-translucent p-card-padding-xs backdrop-blur-[50px] md:absolute md:bottom-200 md:left-1/2 md:w-[615px] md:-translate-x-1/2 md:flex-row md:items-center md:gap-x-inline-m">
						<div className="flex w-full flex-col items-start gap-y-stack-2xs md:min-w-0 md:flex-1">
							<Title
								tabIndex={isActive ? 0 : -1}
								className="text-h3 line-clamp-1 md:line-clamp-2 w-full text-text-heading focus-visible:outline-2 focus-visible:outline-offset-2"
							>
								<BevSafeText>{card.title}</BevSafeText>
							</Title>
							{card.description && (
								<div className="hidden w-full md:block">
									<p className="text-body2 line-clamp-2 text-text-primary">
										<BevSafeText>{card.description}</BevSafeText>
									</p>
								</div>
							)}
						</div>

						<a
							href={card.ctaHref}
							target={card.ctaNewWindow ? '_blank' : undefined}
							rel={card.ctaNewWindow ? 'noopener' : undefined}
							tabIndex={isActive ? 0 : -1}
							className={getCtaStyles({
								intent: 'outline',
								size: 'small',
								className: 'w-[125px] shrink-0 !h-300 after:absolute after:inset-0',
							})}
						>
							{card.ctaLabel ?? translate('Learn more')}
						</a>
					</div>
				</div>
			</div>
		</li>
	);
}
